import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { blogsData } from "../Blog";
import ErrorPage from './ErrorPage';
import Header from "../Components/Header";
import HomeBlogs from "../Components/HomeBlogs"
import ctaBg from '../Images/blog-cta.webp'
import { FaSearch, FaRegUser } from "react-icons/fa";
import SchemaMarkup from "../Components/SchemaMarkup";
import breadcrumbSchema from '../Schemas/breadcrumbSchema'
import blogSchema from "../Schemas/blogSchema";

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  // Local state for handling the search bar typing
  const [searchTerm, setSearchTerm] = useState("");

  const blog = blogsData.find(item => item.slug === slug);

  useEffect(() => {
    if (!blog) return;
    document.title = `${blog.metaTitle}`;

    const metaDesc = document.querySelector("meta[name='description']");
    if (metaDesc) {
      metaDesc.setAttribute("content", blog.metaDesc);
    }


        let canonical = document.querySelector("link[rel='canonical']");
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute(
    "href",
    `https://ldlsettlementservices.com.au/blog/${slug}`
  );

    const ogTitle = document.querySelector("meta[property='og:title']");
    if (ogTitle) {
      ogTitle.setAttribute("content", blog.metaTitle);
    }

    const ogDesc = document.querySelector("meta[property='og:description']");
    if (ogDesc) {
      ogDesc.setAttribute("content", blog.metaDesc);
    }


  const ogUrl = document.querySelector("meta[property='og:url']");
  if (ogUrl) {
    ogUrl.setAttribute("content",  `https://ldlsettlementservices.com.au/blog/${slug}`);
  }

const ogImage = document.querySelector("meta[property='og:image']");
if (ogImage && blog?.image) {
  ogImage.setAttribute(
    "content",
    blog.image.startsWith("http")
      ? blog.image
      : `https://ldlsettlementservices.com.au${blog.image}`
  );
}

    const twitterTitle = document.querySelector("meta[name='twitter:title']");
    if (twitterTitle) {
      twitterTitle.setAttribute("content", blog.metaTitle);
    }

    const twitterDesc = document.querySelector("meta[name='twitter:description']");
    if (twitterDesc) {
      twitterDesc.setAttribute("content", blog.metaDesc);
    }

    const twitterImage = document.querySelector(
  "meta[name='twitter:image']"
);

if (twitterImage) {
  twitterImage.setAttribute(
    "content",
    window.location.origin + blog.image
  );
}
  }, [blog, slug]);

  if (!blog) {
    return <ErrorPage />;
  }

  // Extract static clean categories list from data safely
  const safeBlogsData = Array.isArray(blogsData) ? blogsData : [];
  const categoriesList = [];
  safeBlogsData.forEach((item) => {
    if (item && item.category && !categoriesList.includes(item.category)) {
      categoriesList.push(item.category);
    }
  });

  // Dynamic suggestions logic for the dropdown list box
  const suggestions = [];
  const cleanTerm = searchTerm.trim().toLowerCase();
  if (cleanTerm !== "") {
    categoriesList.forEach((cat) => {
      if (cat.toLowerCase().includes(cleanTerm)) {
        suggestions.push({ type: "category", value: cat });
      }
    });

    safeBlogsData.forEach((item) => {
      if (item && item.title && item.title.toLowerCase().includes(cleanTerm)) {
        suggestions.push({ type: "blog", value: item.title, slug: item.slug });
      }
    });
  }

  // Redirection Router Logic when hitting "Enter" or clicking search icon
// Redirection Router Logic when hitting "Enter" or clicking search icon
  const handleSearchSubmit = () => {
    if (suggestions.length > 0) {
      const topResult = suggestions[0];
      
      if (topResult.type === "category") {
        // Redirect right to the grid section passing category state
        navigate("/blog#blog-grid", { state: { selectCategory: topResult.value } });
      } else if (topResult.type === "blog") {
        navigate(`/blog/${topResult.slug}`);
        setSearchTerm("");
      }
    } else if (searchTerm.trim() !== "") {
      // Pass text search directly down to the grid section
      navigate("/blog#blog-grid", { state: { searchString: searchTerm } });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  // Explicit Category Redirection Click
  const handleCategoryClick = (categoryName) => {
    navigate("/blog#blog-grid", { state: { selectCategory: categoryName } });
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 45 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.12,
        duration: 0.9,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
  };

  const breadcrumbs = breadcrumbSchema([
  {
    name: "Home",
    url: "https://ldlsettlementservices.com.au/"
  },
  {
    name: "Blogs",
    url: "https://ldlsettlementservices.com.au/blogs"
  },
  {
    name: blog.title
  }
]);

const articleSchema = blogSchema(blog, slug);

  return (
    <>
      <React.Fragment key={slug}>

<SchemaMarkup schema={breadcrumbs} />
<SchemaMarkup schema={articleSchema} />

        {/**HEADER */}
        <Header
          showBreadcrumb={true}
          breadcrumbItems={[
            { label: "Blog", link: "/blog" },
            { label: blog.title },
          ]}
        />

        {/**MAIN PAGE STRUCTURE */}
        <section className="blog-detail-section container">
          
          {/* LEFT CONTENT BLOCK */}
          <motion.div
            className="blog-detail-left"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.12 } },
            }}
          >
            <motion.h1 className="hero-title" variants={fadeUp}>
              {blog.title}
            </motion.h1>

            <motion.p className="section-text" variants={fadeUp} custom={1}>
              {blog.introDesc}
            </motion.p>

            <motion.p className="blog-detail-intro" variants={fadeUp} custom={2}>
              {blog.shortDesc}
            </motion.p>

            <motion.div
              className="blog-detail-image"
              variants={fadeUp}
              custom={3}
              whileHover={{ y: -4 }}
              transition={{ duration: 0.35 }}
            >
              <motion.img
                src={blog.image}
                alt={`${blog.title} | LDL Settlement Services`}
                whileHover={{ scale: 1.04 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              />
            </motion.div>

            <motion.div
              className="blog-detail-content"
              variants={fadeUp}
              custom={4}
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
          </motion.div>

          {/* RIGHT SIDEBAR WRAPPER */}
          <motion.div
            className="blog-page-sidebar"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true, amount: 0.15 }}
          >
            {/* AUTHOR META CARD */}
            <motion.div className="blog-detail-author" whileHover={{ y: -4 }} transition={{ duration: 0.3 }}>
              <motion.div
                className="blog-author-icon"
                whileHover={{ rotate: 8, scale: 1.08 }}
                transition={{ duration: 0.35 }}
              >
                <FaRegUser />
              </motion.div>
              <div>
                <h3 className="card-heading">LDL Admin</h3>
                <p className="section-text">Posted on {blog.date}</p>
              </div>
            </motion.div>

            {/* INTERACTIVE NAVIGATION ELEMENTS */}
            <motion.div
              className="blog-page-right"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              viewport={{ once: true }}
            >
              <motion.div className="blog-right-categories" whileHover={{ y: -4 }} transition={{ duration: 0.3 }}>
                
                {/* SEARCH MECHANISM */}
                <div className="blog-page-search">
                  <input
                    type="text"
                    placeholder="Search Category / Articles"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />

                  {suggestions.length > 0 && (
                    <div className="blog-search-dropdown">
                      {suggestions.map((item, idx) => (
                        <div
                          key={`suggest-${idx}`}
                          className="blog-search-item"
                          onClick={() => {
                            if (item.type === "category") {
                              handleCategoryClick(item.value);
                            } else {
                              navigate(`/blog/${item.slug}`);
                              setSearchTerm("");
                            }
                          }}
                        >
                          <span>{item.type === "category" ? "Category" : "Blog"}</span>
                          {item.value}
                        </div>
                      ))}
                    </div>
                  )}

                  <motion.div 
                    whileHover={{ scale: 1.08 }} 
                    transition={{ duration: 0.25 }}
                    onClick={handleSearchSubmit}
                    style={{ cursor: "pointer" }}
                  >
                    <FaSearch className="blog-page-search-icon" />
                  </motion.div>
                </div>

                {/* CATEGORIES ROUTER NAVIGATION LIST */}
                <div className="blog-page-categories">
                  <h3 className="card-heading">Categories</h3>
                  <div className="blog-page-category-list">
                    {categoriesList.map((cat, i) => (
                      <motion.div
                        key={cat}
                        initial={{ opacity: 0, x: -15 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.08, duration: 0.5 }}
                        viewport={{ once: true }}
                      >
                        <span
                          className="section-text"
                          onClick={() => handleCategoryClick(cat)}
                          style={{ cursor: "pointer", display: "block" }}
                        >
                          {cat}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>

              </motion.div>

              {/* CALL TO ACTION ACCENT */}
              <motion.div
                className="blog-page-cta"
                style={{ backgroundImage: `linear-gradient(#C8B692B5, #18263C), url(${ctaBg})` }}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true, amount: 0.2 }}
                whileHover={{ y: -5 }}
              >
                <div className="blog-page-cta-content">
                  <motion.h3 className="card-heading">Need Help with Your Settlement?</motion.h3>
                  <motion.p className="section-text">
                    Our experienced team is here to make the process smooth and stress-free.
                  </motion.p>
                  <div className="blog-page-cta-btns">
                    <Link to="/get-a-quote" className="primary-btn">Get a Quote</Link>
                    <Link to="/contact-us" className="secondary-btn">Contact us</Link>
                  </div>
                </div>
              </motion.div>

            </motion.div>
          </motion.div>

        </section>

        {/**RECURSIVE EXCLUSION GRID */}
        <HomeBlogs excludeSlug={blog.slug} />
      </React.Fragment>
    </>
  );
};

export default BlogDetail;